import { useCallback, useEffect, useMemo, useState } from "react";
import { hasValue } from "../../../util/pocUtils";

export const useQuestionnaireNavigation = ({
   tabKeys,
   activeTabId,
   setActiveTabId,
   formResponseData,
   requiredFieldIds,
   fieldsValue,
   hasTabs,
   isQuestionnaire,
}) => {
   const { tabCompletionStatus } = useMemo(() => {
      if (!hasTabs || !isQuestionnaire) {
         return { tabCompletionStatus: {} };
      }

      const fields = formResponseData?.fields ?? [];
      const requiredIdSet = new Set(requiredFieldIds ?? []);
      const status = {};

      tabKeys.forEach((tabKey) => {
         const tabRequiredFields = fields.filter(
            (field) => String(field.tabIndex) === tabKey && requiredIdSet.has(field.id),
         );

         if (tabRequiredFields.length === 0) {
            status[tabKey] = true;
            return;
         }

         status[tabKey] = tabRequiredFields.every((field) => {
            const value = fieldsValue?.[field.id];
            if (field.type === "Number" || field.type === "Decimal") {
               const trimmed = typeof value === "string" ? value.trim() : value;
               return trimmed !== "" && trimmed != null && !isNaN(Number(trimmed));
            }
            return hasValue(value);
         });
      });

      return { tabCompletionStatus: status };
   }, [tabKeys, hasTabs, isQuestionnaire, formResponseData?.fields, requiredFieldIds, fieldsValue]);

   const rawIndex = tabKeys.indexOf(activeTabId);
   const currentPageIndex = rawIndex === -1 ? 0 : rawIndex;

   const [visitedTabKeys, setVisitedTabKeys] = useState(new Set());

   useEffect(() => {
      setVisitedTabKeys(new Set());
      setActiveTabId(tabKeys[0] ?? null);
   }, [formResponseData?.id]); // eslint-disable-line react-hooks/exhaustive-deps

   const markTabVisited = useCallback((tabKey) => {
      if (!tabKey) return;
      setVisitedTabKeys((prev) => {
         if (prev.has(tabKey)) return prev;
         return new Set(prev).add(tabKey);
      });
   }, []);

   const goToPrev = useCallback(() => {
      if (rawIndex > 0) {
         markTabVisited(tabKeys[rawIndex]);
         setActiveTabId(tabKeys[rawIndex - 1]);
      }
   }, [rawIndex, tabKeys, setActiveTabId, markTabVisited]);

   const goToNext = useCallback(() => {
      if (rawIndex >= 0 && rawIndex < tabKeys.length - 1) {
         markTabVisited(tabKeys[rawIndex]);
         setActiveTabId(tabKeys[rawIndex + 1]);
      }
   }, [rawIndex, tabKeys, setActiveTabId, markTabVisited]);

   return {
      tabCompletionStatus,
      currentPageIndex,
      goToPrev,
      goToNext,
      visitedTabKeys,
      markTabVisited,
   };
};
